/**
 * An instant, as the viewer's calendar shows it.
 *
 * Right for when something happened (`createdAt`, `sentAt`). A day the
 * business named goes through `formatUtcDate`: a midnight UTC value reads
 * as the day before in Central time.
 */
export function formatDate(
  value: string | Date,
  locale = 'en-US',
): string {
  const date = typeof value === 'string' ? new Date(value) : value
  return date.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

/**
 * A calendar date, read in UTC.
 *
 * The month, day, and year options stay with the caller. The zone does not:
 * every caller was pinning `timeZone: 'UTC'` on its own, and one that forgot
 * showed the previous day.
 */
export function formatUtcDate(
  value: string | Date,
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  },
  locale = 'en-US',
): string {
  const date = typeof value === 'string' ? new Date(value) : value
  return date.toLocaleDateString(locale, { ...options, timeZone: 'UTC' })
}

/**
 * An instant as the Central calendar shows it.
 *
 * The day a crew walked a property. Smithwell works in America/Chicago, so
 * an evening walk is still that day in Lubbock after UTC has rolled over.
 * A calendar day the business named — a meeting date, the day a price holds
 * until — stays on `formatUtcDate`.
 */
export function formatCentralDate(
  value: string | Date,
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  },
  locale = 'en-US',
): string {
  const date = typeof value === 'string' ? new Date(value) : value
  return date.toLocaleDateString(locale, {
    ...options,
    timeZone: 'America/Chicago',
  })
}

/**
 * Date and time of day, for events where the hour matters.
 *
 * A proposal delivery is one: "sent 29 Sep" and "sent 29 Sep, 4:12 PM" answer
 * different questions when somebody is working out whether a client had it
 * before a board meeting.
 */
export function formatDateTime(
  value: string | Date,
  locale = 'en-US',
): string {
  const date = typeof value === 'string' ? new Date(value) : value
  return date.toLocaleString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}
